import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Building2, Users, TrendingUp, ClipboardCheck, MapPin, Phone, Mail, User, Briefcase, Calendar, FileText, Receipt, PenTool, CheckCircle, Search, ExternalLink, Download, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import ZenButton from '../components/ZenButton';
import NotificationWidget from '../components/NotificationWidget';
import AutonomieActionCard from '../components/AutonomieActionCard';
import StagiaireAccordionCard from '../components/entreprise/StagiaireAccordionCard';
import { openPdfBlob } from '../utils/pdfOpener';

const DashboardEntreprise = ({ user }) => {
  const [stagiaires, setStagiaires] = useState([]);
  const [profile, setProfile] = useState(null);
  const [convStageList, setConvStageList] = useState([]);
  const [unreadDocCounts, setUnreadDocCounts] = useState({});
  const [stagiaireAlerts, setStagiaireAlerts] = useState({});

  // Devis et Factures
  const [mesDevis, setMesDevis] = useState([]);
  const [mesFactures, setMesFactures] = useState([]);
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [selectedDevis, setSelectedDevis] = useState(null);
  const signatureRef = useRef(null);

  // Convention signature dialog
  const [showSignConvStageDialog, setShowSignConvStageDialog] = useState(false);
  const [selectedConvStagiaire, setSelectedConvStagiaire] = useState(null);
  const [useProfileSig, setUseProfileSig] = useState(false);
  const [savedSignature, setSavedSignature] = useState(null);
  const [savedTampon, setSavedTampon] = useState(null);
  const convStageSignRef = useRef(null);

  // Diplome bravo
  const [showDiplomeBravo, setShowDiplomeBravo] = useState(false);
  const [diplomeStagiaireNom, setDiplomeStagiaireNom] = useState('');

  useEffect(() => {
    loadData();
    loadMesDevis();
    loadSavedSignatureAndTampon();
  }, []);

  useEffect(() => {
    if (stagiaires.length > 0) checkDiplomeStagiaires();
  }, [stagiaires]);

  const checkDiplomeStagiaires = async () => {
    const dismissed = localStorage.getItem(`diplome_bravo_entreprise_${user.user_id}`);
    if (dismissed) return;
    for (const s of stagiaires) {
      try {
        const res = await axios.get(`/stagiaires/${s.user_id}/competences-prerequis`);
        if (res.data.diplome_obtenu) {
          setDiplomeStagiaireNom(`${s.user_info?.prenom || s.prenom || ''} ${s.user_info?.nom || s.nom || ''}`);
          setShowDiplomeBravo(true);
          break;
        }
      } catch {}
    }
  };

  const dismissDiplomeBravo = () => {
    setShowDiplomeBravo(false);
    sessionStorage.setItem(`diplome_bravo_entreprise_${user.user_id}`, 'true');
    localStorage.setItem(`diplome_bravo_entreprise_${user.user_id}`, 'true');
  };

  const loadData = async () => {
    try {
      const [stagRes, profRes] = await Promise.all([
        axios.get('/stagiaires'),
        axios.get(`/entreprises/${user.user_id}`)
      ]);

      setStagiaires(stagRes.data);
      setProfile(profRes.data.profile || {});

      // Load conventions per stagiaire
      const convList = [];
      for (const stag of stagRes.data) {
        try {
          const convRes = await axios.get(`/convention-stage/${stag.user_id}`);
          if (convRes.data && convRes.data.statut) {
            convList.push({
              ...convRes.data,
              stagiaire_id: stag.user_id,
              stagiaire_nom: stag.nom || stag.user_info?.nom || '',
              stagiaire_prenom: stag.prenom || stag.user_info?.prenom || ''
            });
          }
        } catch {}
      }
      setConvStageList(convList);

      // Load unread document counts
      const docCounts = {};
      for (const stag of stagRes.data) {
        try {
          const countRes = await axios.get(`/entreprise/stagiaire/${stag.user_id}/documents/count-unread`);
          docCounts[stag.user_id] = countRes.data?.count || 0;
        } catch { docCounts[stag.user_id] = 0; }
      }
      setUnreadDocCounts(docCounts);

      // Load notifications to detect new activity per stagiaire
      try {
        const notifRes = await axios.get('/notifications');
        const unreadNotifs = (notifRes.data || []).filter(n => !n.lu && !n.read);
        const alerts = {};
        for (const stag of stagRes.data) {
          const stagNom = `${stag.user_info?.prenom || stag.prenom || ''} ${stag.user_info?.nom || stag.nom || ''}`.toLowerCase();
          const related = unreadNotifs.filter(n =>
            (n.message || '').toLowerCase().includes(stagNom) ||
            (n.message || '').includes(stag.user_id)
          );
          alerts[stag.user_id] = (docCounts[stag.user_id] || 0) + related.length;
        }
        setStagiaireAlerts(alerts);
      } catch {
        setStagiaireAlerts({});
      }
    } catch (error) {
    }
  };

  const loadMesDevis = async () => {
    try {
      const response = await axios.get('/stagiaire/mes-devis');
      setMesDevis(response.data.devis || []);
      setMesFactures(response.data.factures || []);
    } catch (error) {
    }
  };

  const loadSavedSignatureAndTampon = async () => {
    try {
      const [sigRes, tamponRes, profilRes] = await Promise.all([
        axios.get(`/signatures/${user.user_id}`).catch(() => ({ data: {} })),
        axios.get('/entreprise/tampon').catch(() => ({ data: {} })),
        axios.get(`/entreprises/${user.user_id}`).catch(() => ({ data: {} }))
      ]);
      // Priorité: signature du profil entreprise (gerant_signature) > endpoint signatures
      const profilSig = profilRes.data?.profile?.gerant_signature;
      if (profilSig) {
        setSavedSignature(profilSig);
      } else if (sigRes.data?.signature) {
        setSavedSignature(sigRes.data.signature);
      }
      if (tamponRes.data?.tampon) {
        setSavedTampon(tamponRes.data.tampon);
      } else if (profilRes.data?.profile?.tampon) {
        setSavedTampon(profilRes.data.profile.tampon);
      }
    } catch {}
  };

  const handleDownloadDevis = async (devisId) => {
    try {
      const response = await axios.get(`/stagiaire/devis/${devisId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${user?.nom || 'entreprise'}_${user?.prenom || ''}_devis.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Devis téléchargé');
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const openSignatureDialog = (devis) => {
    setSelectedDevis(devis);
    setShowSignatureDialog(true);
  };

  const handleSignerDevis = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast.error('Veuillez dessiner votre signature');
      return;
    }
    const signature = signatureRef.current.toDataURL();
    try {
      await axios.post(`/stagiaire/devis/${selectedDevis.devis_id}/signer`, null, { params: { signature } });
      toast.success('Devis signé avec succès !');
      setShowSignatureDialog(false);
      loadMesDevis();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    }
  };

  const clearSignature = () => {
    if (signatureRef.current) signatureRef.current.clear();
  };

  const handleSignConvStage = async () => {
    if (!savedTampon) {
      toast.error("Un tampon d'entreprise est requis. Rendez-vous dans votre Profil pour le générer.");
      return;
    }
    let signature;
    if (useProfileSig && savedSignature) {
      signature = savedSignature;
    } else {
      if (!convStageSignRef.current || convStageSignRef.current.isEmpty()) {
        toast.error('Veuillez dessiner votre signature');
        return;
      }
      signature = convStageSignRef.current.toDataURL();
    }
    try {
      const payload = { signature, tampon: savedTampon };
      await axios.post(`/convention-stage/${selectedConvStagiaire.stagiaire_id}/signer`, payload);
      toast.success('Convention de Mise en Situation Pédagogique signée avec tampon !');
      setShowSignConvStageDialog(false);
      setSelectedConvStagiaire(null);
      setUseProfileSig(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    }
  };

  const handleViewConvStage = async (stagiaireId, stagiaireNom = 'stagiaire') => {
    try {
      const res = await axios.get(`/convention-stage/${stagiaireId}/pdf`, { responseType: 'blob' });
      openPdfBlob(res.data, `Convention_${stagiaireNom}.pdf`, 'view');
    } catch {
      toast.error('Impossible de charger le PDF');
    }
  };

  const handleDownloadConvStage = async (stagiaireId, stagiaireNom = 'stagiaire') => {
    try {
      const res = await axios.get(`/convention-stage/${stagiaireId}/pdf`, { responseType: 'blob' });
      openPdfBlob(res.data, `Convention_${stagiaireNom}.pdf`, 'download');
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleOpenSignConvDialog = (conv) => {
    setSelectedConvStagiaire(conv);
    setShowSignConvStageDialog(true);
  };

  return (
    <div className="space-y-8" data-testid="dashboard-entreprise">
      {/* BANNIERE Conventions en attente de signature */}
      {convStageList.filter(c => c.convention && (c.convention.statut === 'en_attente_signatures' || c.convention.statut === 'partiellement_signe') && !c.convention.signature_entreprise).length > 0 && (
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-5 text-white shadow-lg" data-testid="banner-convention-entreprise">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 rounded-full p-3">
              <PenTool className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold">Convention(s) à signer</h3>
              <p className="text-white/90 text-sm">
                {convStageList.filter(c => c.convention && (c.convention.statut === 'en_attente_signatures' || c.convention.statut === 'partiellement_signe') && !c.convention.signature_entreprise).map(c => `${c.stagiaire_prenom} ${c.stagiaire_nom}`).join(', ')} — Convention de Mise en Situation Pédagogique en attente de votre signature et tampon.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-display font-bold text-[#0F172A] mb-2">Espace Entreprise</h1>
          <p className="text-slate-600">Bienvenue dans votre espace de gestion</p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="https://recherche-educateur.sports.gouv.fr/accueil"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
          >
            <Search className="h-4 w-4" />
            Recherche d'éducateur sportif
            <ExternalLink className="h-3 w-3" />
          </a>
          <ZenButton />
        </div>
      </div>

      {/* Profil Entreprise Bandeau */}
      <div className="bg-gradient-to-r from-[#0f4c81] to-[#1a6fb5] rounded-lg p-6 shadow-sm text-white">
        <div className="flex items-start gap-6">
          <div className="w-24 h-24 bg-white/20 border-2 border-white/30 rounded-lg overflow-hidden flex-shrink-0">
            {profile?.logo ? (
              <img src={profile.logo} alt="Logo" className="w-full h-full object-contain p-2" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Building2 className="h-12 w-12 text-white/60" />
              </div>
            )}
          </div>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h2 className="text-xl font-semibold text-white mb-2">{profile?.nom || 'Mon Entreprise'}</h2>
              {profile?.adresse && (
                <p className="text-sm text-white/80 flex items-center gap-2 mb-1">
                  <MapPin className="h-4 w-4 text-[#8DC63F]" /> {profile.adresse}
                </p>
              )}
              {profile?.telephone && (
                <p className="text-sm text-white/80 flex items-center gap-2 mb-1">
                  <Phone className="h-4 w-4 text-[#8DC63F]" /> {profile.telephone}
                </p>
              )}
              {profile?.email && (
                <p className="text-sm text-white/80 flex items-center gap-2">
                  <Mail className="h-4 w-4 text-[#8DC63F]" /> {profile.email}
                </p>
              )}
            </div>
            <div>
              {(profile?.gerant_prenom || profile?.gerant_nom) && (
                <div className="mb-3">
                  <p className="text-xs text-white/60 uppercase tracking-wide">Gérant</p>
                  <p className="text-sm font-medium text-white flex items-center gap-2">
                    <User className="h-4 w-4 text-[#8DC63F]" />
                    {[profile.gerant_genre, profile.gerant_prenom, profile.gerant_nom].filter(Boolean).join(' ')}
                  </p>
                </div>
              )}
              {profile?.activites && (
                <div>
                  <p className="text-xs text-white/60 uppercase tracking-wide mb-1">Activités</p>
                  <div className="flex flex-wrap gap-1">
                    {profile.activites.randonnee_vnm && (
                      <span className="px-2 py-0.5 bg-[#8DC63F]/20 text-[#8DC63F] text-xs rounded-full border border-[#8DC63F]/30">
                        VNM ({profile.activites.nombre_vnm || 0})
                      </span>
                    )}
                    {profile.activites.engins_tractes && (
                      <span className="px-2 py-0.5 bg-blue-400/20 text-blue-200 text-xs rounded-full border border-blue-400/30">Engins tractés</span>
                    )}
                    {profile.activites.pnm && (
                      <span className="px-2 py-0.5 bg-purple-400/20 text-purple-200 text-xs rounded-full border border-purple-400/30">PNM</span>
                    )}
                    {profile.activites.flyboard && (
                      <span className="px-2 py-0.5 bg-orange-400/20 text-orange-200 text-xs rounded-full border border-orange-400/30">Flyboard</span>
                    )}
                    {profile.activites.location_bateau && (
                      <span className="px-2 py-0.5 bg-cyan-400/20 text-cyan-200 text-xs rounded-full border border-cyan-400/30">Location bateau</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <Link
            to="/profil-entreprise"
            className="px-4 py-2 border border-white/30 rounded-lg text-sm text-white hover:bg-white/10 transition-colors"
          >
            Modifier
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm border-l-4 border-l-[#8DC63F]" data-testid="stat-stagiaires">
          <div className="flex items-center space-x-4">
            <div className="bg-[#8DC63F]/10 p-3 rounded-lg">
              <Users className="h-6 w-6 text-[#8DC63F]" />
            </div>
            <div>
              <p className="text-3xl font-display font-bold text-[#0F172A]">{stagiaires.length}</p>
              <p className="text-sm text-slate-600">Stagiaires en formation</p>
            </div>
          </div>
        </div>
        <Link to="/planning" className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm border-l-4 border-l-blue-500 hover:shadow-md transition-shadow block" data-testid="stat-planning">
          <div className="flex items-center space-x-4">
            <div className="bg-blue-500/10 p-3 rounded-lg">
              <Calendar className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <p className="text-lg font-semibold text-[#0F172A]">Planning</p>
              <p className="text-sm text-slate-600">Voir le planning</p>
            </div>
          </div>
        </Link>
        <AutonomieActionCard linkTo="/validation-autonomie" />
        <NotificationWidget />
      </div>

      {/* Mes Stagiaires - Vue groupée par stagiaire */}
      <div className="space-y-4">
        <div className="flex items-center space-x-3">
          <Users className="h-6 w-6 text-[#8DC63F]" />
          <h2 className="text-2xl font-display font-semibold text-[#0F172A]">Mes Stagiaires</h2>
          <span className="text-sm text-slate-400">Cliquez sur un stagiaire pour voir tous ses documents</span>
        </div>
        {stagiaires.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
            <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Aucun stagiaire assigné</p>
          </div>
        ) : (
          <div className="space-y-3">
            {stagiaires.map((stagiaire, index) => (
              <StagiaireAccordionCard
                key={stagiaire.user_id}
                stagiaire={stagiaire}
                index={index}
                convention={convStageList.find(c => c.stagiaire_id === stagiaire.user_id)}
                unreadDocCount={unreadDocCounts[stagiaire.user_id] || 0}
                newActivityCount={stagiaireAlerts[stagiaire.user_id] || 0}
                onSignConvention={handleOpenSignConvDialog}
                onViewConvention={handleViewConvStage}
                onDownloadConvention={handleDownloadConvStage}
              />
            ))}
          </div>
        )}
      </div>

      {/* Actions Rapides */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center space-x-3 mb-6">
          <Briefcase className="h-6 w-6 text-[#8DC63F]" />
          <h2 className="text-2xl font-display font-semibold text-[#0F172A]">Actions Rapides</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/suivi-stagiaire" className="p-4 border border-slate-200 rounded-lg hover:border-[#8DC63F] transition-colors text-center" data-testid="quick-link-suivi">
            <TrendingUp className="h-8 w-8 text-[#8DC63F] mx-auto mb-2" />
            <p className="font-semibold text-[#0F172A]">Notes & Historique</p>
            <p className="text-sm text-slate-600">Consulter les évaluations</p>
          </Link>
          <Link to="/validation-autonomie" className="p-4 border border-slate-200 rounded-lg hover:border-[#8DC63F] transition-colors text-center" data-testid="quick-link-validation">
            <ClipboardCheck className="h-8 w-8 text-orange-500 mx-auto mb-2" />
            <p className="font-semibold text-[#0F172A]">Valider Autonomie</p>
            <p className="text-sm text-slate-600">Accepter les validations</p>
          </Link>
          <Link to="/presence-entreprise" className="p-4 border border-slate-200 rounded-lg hover:border-[#8DC63F] transition-colors text-center" data-testid="quick-link-presence">
            <Calendar className="h-8 w-8 text-green-500 mx-auto mb-2" />
            <p className="font-semibold text-[#0F172A]">Présence stagiaires</p>
            <p className="text-sm text-slate-600">Consulter les présences</p>
          </Link>
          <Link to="/profil-entreprise" className="p-4 border border-slate-200 rounded-lg hover:border-[#8DC63F] transition-colors text-center" data-testid="quick-link-profil">
            <Building2 className="h-8 w-8 text-blue-500 mx-auto mb-2" />
            <p className="font-semibold text-[#0F172A]">Mon Profil</p>
            <p className="text-sm text-slate-600">Modifier mes infos</p>
          </Link>
        </div>
      </div>

      {/* Mes Devis et Factures */}
      {(mesDevis.length > 0 || mesFactures.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {mesDevis.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-[#8DC63F]" />
                  Devis de Formation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {mesDevis.map((devis, idx) => (
                    <div key={idx} className="p-4 bg-green-50 rounded-lg border border-green-100" data-testid={`entreprise-devis-${devis.devis_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-sm bg-green-100 px-2 py-1 rounded">#{devis.devis_id}</span>
                        <Badge className={
                          devis.statut === 'accepte' ? 'bg-green-500' :
                          devis.statut === 'envoye' ? 'bg-blue-500' :
                          devis.statut === 'refuse' ? 'bg-red-500' : 'bg-slate-500'
                        }>
                          {devis.statut === 'accepte' ? 'Accepté' :
                           devis.statut === 'envoye' ? 'En attente' :
                           devis.statut === 'refuse' ? 'Refusé' : 'Brouillon'}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center mb-3">
                        <div className="text-sm text-slate-600">
                          <p>Créé le: {new Date(devis.date_creation).toLocaleDateString('fr-FR')}</p>
                          {devis.bc_codes?.length > 0 && <p className="text-xs">BC: {devis.bc_codes.join(', ')}</p>}
                        </div>
                        <p className="text-xl font-bold text-green-700">{(devis.montant_total || 0).toLocaleString('fr-FR')} EUR</p>
                      </div>
                      {devis.statut === 'accepte' && devis.signature && (
                        <div className="mb-3 p-2 bg-green-100 rounded text-sm text-green-700">
                          Signé par {devis.accepte_par || "l'entreprise"} le {new Date(devis.date_acceptation).toLocaleDateString('fr-FR')}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleDownloadDevis(devis.devis_id)} data-testid={`entreprise-download-devis-${devis.devis_id}`}>
                          <Download className="h-4 w-4 mr-1" /> Télécharger
                        </Button>
                        {devis.statut !== 'accepte' && devis.statut !== 'refuse' && (
                          <Button size="sm" className="bg-[#8DC63F] hover:bg-[#7AB62F]" onClick={() => openSignatureDialog(devis)} data-testid={`entreprise-signer-devis-${devis.devis_id}`}>
                            <PenTool className="h-4 w-4 mr-1" /> Signer et accepter
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {mesFactures.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5 text-blue-600" />
                  Factures
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {mesFactures.map((facture, idx) => (
                    <div key={idx} className="p-4 bg-blue-50 rounded-lg border border-blue-100" data-testid={`entreprise-facture-${facture.facture_numero}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-sm bg-blue-100 px-2 py-1 rounded">{facture.facture_numero}</span>
                        <Badge className={
                          facture.statut === 'payee' ? 'bg-green-500' :
                          facture.statut === 'partielle' ? 'bg-orange-500' : 'bg-blue-500'
                        }>
                          {facture.statut === 'payee' ? 'Payée' :
                           facture.statut === 'partielle' ? 'Partiellement payée' : 'En attente'}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="text-sm text-slate-600">
                          <p>{new Date(facture.date_creation).toLocaleDateString('fr-FR')}</p>
                          {(facture.total_paye || 0) > 0 && (
                            <p className="text-xs text-green-600">Payé: {facture.total_paye.toLocaleString('fr-FR')} EUR</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-blue-700">{(facture.montant_total || 0).toLocaleString('fr-FR')} EUR</p>
                          {facture.statut !== 'payee' && (
                            <p className="text-xs text-orange-600">Reste: {(facture.solde_restant || facture.montant_total).toLocaleString('fr-FR')} EUR</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Dialogue de signature devis */}
      <Dialog open={showSignatureDialog} onOpenChange={setShowSignatureDialog}>
        <DialogContent className="max-w-lg" data-testid="entreprise-signature-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="h-5 w-5 text-[#8DC63F]" />
              Signature électronique - Bon pour acceptation
            </DialogTitle>
            <DialogDescription>
              Devis #{selectedDevis?.devis_id} - {(selectedDevis?.montant_total || 0).toLocaleString('fr-FR')} EUR
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-slate-700 mb-2">
                En signant ce devis, <strong>{[profile?.gerant_prenom, profile?.gerant_nom].filter(Boolean).join(' ') || profile?.nom || "l'entreprise"}</strong> accepte les conditions de la formation.
              </p>
              <p className="text-xs text-slate-500">Date: {new Date().toLocaleDateString('fr-FR')}</p>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Dessinez votre signature ci-dessous:</label>
              <div className="border-2 border-slate-300 rounded-lg overflow-hidden bg-white">
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{ className: 'w-full h-40 bg-white', style: { touchAction: 'none' } }}
                  penColor="#333"
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">Utilisez votre doigt ou votre stylet pour signer</p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={clearSignature}>Effacer</Button>
            <Button variant="outline" onClick={() => setShowSignatureDialog(false)}>Annuler</Button>
            <Button onClick={handleSignerDevis} className="bg-[#8DC63F] hover:bg-[#7AB62F]" data-testid="entreprise-confirm-signature-btn">
              <CheckCircle className="h-4 w-4 mr-2" /> Valider et signer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialogue de signature Convention */}
      <Dialog open={showSignConvStageDialog} onOpenChange={setShowSignConvStageDialog}>
        <DialogContent className="max-w-lg" data-testid="signature-conv-stage-entreprise-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="h-5 w-5 text-orange-500" />
              Signature - Convention de Mise en Situation Pédagogique
            </DialogTitle>
            <DialogDescription>
              Convention pour {selectedConvStagiaire?.stagiaire_prenom} {selectedConvStagiaire?.stagiaire_nom}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium mb-2">Tampon de l'entreprise <span className="text-red-500">*</span> :</p>
              {savedTampon ? (
                <div className="border-2 border-blue-300 rounded-lg p-3 bg-blue-50 text-center">
                  <img src={savedTampon} alt="Tampon" className="max-h-24 mx-auto" data-testid="conv-stage-tampon-preview" />
                  <p className="text-xs text-blue-600 mt-1">Tampon enregistré</p>
                </div>
              ) : (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">Aucun tampon généré. Rendez-vous dans votre <Link to="/profil-entreprise" className="font-bold underline">Profil</Link> pour générer un tampon d'entreprise.</p>
                </div>
              )}
            </div>
            <div className="border-t pt-3">
              <p className="text-sm font-medium mb-2">Signature du gérant <span className="text-red-500">*</span> :</p>
              {savedSignature && (
                <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg mb-3">
                  <input type="checkbox" id="useProfileSigEnt" checked={useProfileSig} onChange={(e) => setUseProfileSig(e.target.checked)} className="rounded" />
                  <label htmlFor="useProfileSigEnt" className="text-sm text-green-800 cursor-pointer">Utiliser ma signature enregistrée</label>
                </div>
              )}
              {useProfileSig && savedSignature ? (
                <div className="border-2 border-green-300 rounded-lg p-4 bg-white text-center">
                  <img src={savedSignature} alt="Signature" className="max-h-28 mx-auto" />
                  <p className="text-xs text-green-600 mt-1">Signature enregistrée</p>
                </div>
              ) : (
                <div>
                  <div className="border-2 border-slate-300 rounded-lg overflow-hidden bg-white">
                    <SignatureCanvas ref={convStageSignRef} canvasProps={{ className: 'w-full h-36 bg-white', style: { touchAction: 'none' } }} penColor="#333" />
                  </div>
                  <div className="flex justify-between mt-1">
                    <p className="text-xs text-slate-400">Dessinez votre signature</p>
                    <button onClick={() => convStageSignRef.current?.clear()} className="text-xs text-slate-500 hover:text-red-500">Effacer</button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowSignConvStageDialog(false); setUseProfileSig(false); }}>Annuler</Button>
            <Button
              onClick={handleSignConvStage}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="confirm-signature-conv-stage-entreprise-btn"
              disabled={!savedTampon}
            >
              <CheckCircle className="h-4 w-4 mr-2" /> Valider et signer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Bravo Diplome */}
      <Dialog open={showDiplomeBravo} onOpenChange={dismissDiplomeBravo}>
        <DialogContent className="sm:max-w-lg text-center">
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-lg animate-bounce">
              <Award className="h-10 w-10 text-white" />
            </div>
            <DialogHeader className="space-y-2">
              <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-amber-500 to-yellow-600 bg-clip-text text-transparent">
                Bravo !
              </DialogTitle>
              <DialogDescription className="text-base text-[#0F172A] font-medium">
                Votre stagiaire {diplomeStagiaireNom} a reçu un avis favorable pour l'obtention du diplôme.
              </DialogDescription>
            </DialogHeader>
            <p className="text-base font-semibold text-[#8DC63F]">
              Merci pour votre engagement et votre accompagnement !
            </p>
          </div>
          <DialogFooter className="justify-center">
            <Button onClick={dismissDiplomeBravo} className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white px-8" data-testid="dismiss-diplome-bravo-btn">
              Merci !
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardEntreprise;
